import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import AuthService from "services/AuthService";
import {
  ENABLE_SAAS_FEATURE,
  DEFAULT_ORGANIZATION_ID,
} from "configs/AppConfig";

export const initialState = {
  loading: true,
  message: "",
  showMessage: false,
  redirect: "",
  expiresAt: null,
  isAuthenticated: false,
  token: localStorage.getItem(AUTH_TOKEN) || null,
  userData: null,
  lawFirmId: null,
  organizationId: null,
  unsavedChangesChecker: null,
  organizationRefreshTrigger: 0,
};

/*
Author: vipul.singh@velsof.com
Purpose: Validate login by refreshing token
*/
export const validateLogin = createAsyncThunk(
  "auth/refresh",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.refreshToken();
      if (response.status === true) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        return {
          status: response.status,
          expiresAt,
        };
      } else {
        return rejectWithValue(response?.data?.message || "Error");
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In
*/
export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.login({ data });
      if (response.status === true) {
        const expiresAt = Date.now() + response.data.expires_in * 1000;
        return {
          user: response.data.user,
          expiresAt,
        };
      }
      return rejectWithValue(response.message || "Login failed");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign Up, current not in use
*/
export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.register({ email, password });
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign Out
*/
export const signOut = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.logout();

      if (response.status) {
        return {
          status: response,
        };
      } else {
        return rejectWithValue("Logout failed");
      }
    } catch (error) {
      return rejectWithValue(error.response?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In with Google OAuth, currently not in use
*/
export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: User Sign In with Facebook OAuth, currently not in use
*/
export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: Fetch current user details
*/
export const fetchWhoami = createAsyncThunk(
  "auth/whoami",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.whoami();
      if (response.status === true) {
        let userData = response.data;

        // In single-tenant mode, pin the user to the configured org ID directly without API call | By vipul.singh@velsof.com on 29-04-2026
        if (!ENABLE_SAAS_FEATURE) {
          userData = {
            ...userData,
            organisations: [{ id: DEFAULT_ORGANIZATION_ID }],
          };
        }

        return userData;
      }
      return rejectWithValue(response.message || "Failed to fetch user data");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (data, { rejectWithValue }) => {
    try {
      const response = await AuthService.resetPassword({ data });
      if (response.status === true) {
        return {
          status: response.status,
          message: response.message,
        };
      }
      return rejectWithValue(response.message || "Password Reset Failed!");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

/*
Author: vipul.singh@velsof.com
Purpose: Creating auth slice for Redux Toolkit, handling authentication state and extra reducers
*/
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
      state.isAuthenticated = true;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    setUserData: (state, action) => {
      state.userData = action.payload;
    },
    setOrganizationId: (state, action) => {
      state.organizationId = action.payload;
    },
    setUnsavedChangesChecker: (state, action) => {
      state.unsavedChangesChecker = action.payload;
    },
    refreshOrganizations: (state) => {
      state.organizationRefreshTrigger += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.isAuthenticated = true;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.redirect = "/";
        state.userData = null;
        state.lawFirmId = null;
        state.organizationId = null;
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
        state.userData = null;
        state.lawFirmId = null;
        state.organizationId = null;
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(validateLogin.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.expiresAt = action?.payload?.expiresAt;
        state.loading = false;
      })
      .addCase(validateLogin.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.expiresAt = null;
        state.loading = false;
      })
      .addCase(fetchWhoami.pending, (state) => {
      })
      .addCase(fetchWhoami.fulfilled, (state, action) => {
        state.userData = action.payload;
        const lawFirm = action.payload?.law_firms?.[0];
        state.lawFirmId = lawFirm?.id;

        // Only set organizationId if not already set (e.g., from URL param)
        // This allows NavOrganization to set it from URL before this runs
        if (!state.organizationId) {
          const organisation = action.payload?.organisations?.[0];
          state.organizationId = organisation?.id;
        }
      })
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.message = action.payload.message;
        state.redirect = "/";
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

/*
Author: vipul.singh@velsof.com
Purpose: Exporting actions and reducer from auth slice
*/
export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setUserData,
  setOrganizationId,
  setUnsavedChangesChecker,
  refreshOrganizations,
} = authSlice.actions;

export default authSlice.reducer;
